import { action, makeObservable, observable, runInAction } from "mobx"

import type { IOrchestratedQuerySender } from "./orchestrated-query-interfaces"
import type { OrchestratedQueryStore } from "./orchestrated-query-store"
import type { RpcExecutionMetrics } from "./rpc-metrics"
import { RpcMetricsCollector } from "./rpc-metrics"

/**
 * Rate limiting configuration for OrchestratedQuery
 * Ensures minimum time between executions to prevent API rate limiting
 */
export interface RateLimitConfig {
  /**
   * Minimum milliseconds between executions (measured from completion time)
   * Example: { minIntervalMs: 1000 } ensures at least 1 second between requests
   */
  minIntervalMs: number
}

/**
 * Chunk size bounds configuration for queries that use chunked RPC calls
 */
export interface ChunkSizeBounds {
  /**
   * Minimum allowed chunk size for this query type
   */
  min: number
  /**
   * Maximum allowed chunk size for this query type
   */
  max: number
  /**
   * Default chunk size to use initially
   */
  default: number
}

/**
 * A query that executes at controlled intervals and reports results via callbacks.
 * Orchestrated by external controllers (like TokenOrchestrator) for background data fetching.
 */
export class OrchestratedQuery<T> implements IOrchestratedQuerySender<T> {
  private readonly name: string
  private readonly queryFn: (query: IOrchestratedQuerySender<T>) => Promise<T>
  private readonly onQuerySuccess: (sender: IOrchestratedQuerySender<T>, data: T) => void
  private readonly onQueryFailed: (sender: IOrchestratedQuerySender<T>, error: Error) => void
  private readonly orchestratedQueryStore: OrchestratedQueryStore
  private readonly rateLimit?: RateLimitConfig
  private readonly uniqueId: string

  intervalMs: number
  intervalId: ReturnType<typeof setInterval> | null = null
  isRunning = false
  isActive = false
  lastResult: T | undefined
  lastError: Error | undefined
  executionCount = 0
  totalExecutionTime = 0
  lastExecutionTime = 0
  lastExecutionDuration = 0

  // RPC Metrics (observable, owned by query)
  private rpcMetricsCollector: RpcMetricsCollector | undefined
  lastRpcMetrics: RpcExecutionMetrics | undefined
  rpcMetricsHistory: RpcExecutionMetrics[] = []
  private readonly maxMetricsHistory = 10

  // Chunk Size Configuration (for queries that use chunked RPC calls)
  chunkSize: number
  readonly chunkSizeBounds: ChunkSizeBounds | undefined

  constructor(
    name: string,
    queryFn: (query: IOrchestratedQuerySender<T>) => Promise<T>,
    intervalMs: number,
    onQuerySuccess: (sender: IOrchestratedQuerySender<T>, data: T) => void,
    onQueryFailed: (sender: IOrchestratedQuerySender<T>, error: Error) => void,
    orchestratedQueryStore: OrchestratedQueryStore,
    rateLimit?: RateLimitConfig,
    chunkSizeBounds?: ChunkSizeBounds,
  ) {
    // Generate unique ID using crypto.randomUUID() or fallback to timestamp + random
    this.uniqueId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`

    this.name = name
    this.queryFn = queryFn
    this.intervalMs = intervalMs
    this.intervalId = null
    this.isRunning = false
    this.isActive = false
    this.lastResult = undefined
    this.lastError = undefined
    this.executionCount = 0
    this.totalExecutionTime = 0
    this.lastExecutionTime = 0
    this.lastExecutionDuration = 0
    this.lastRpcMetrics = undefined
    this.rpcMetricsHistory = []

    // Initialize chunk size configuration
    if (chunkSizeBounds) {
      this.chunkSizeBounds = chunkSizeBounds
      this.chunkSize = chunkSizeBounds.default
    } else {
      this.chunkSizeBounds = undefined
      this.chunkSize = 0
    }

    this.onQuerySuccess = onQuerySuccess
    this.onQueryFailed = onQueryFailed
    this.orchestratedQueryStore = orchestratedQueryStore
    this.rateLimit = rateLimit

    // Auto-register with the store
    this.orchestratedQueryStore.registerQuery(this)

    makeObservable(this, {
      intervalMs: observable,
      intervalId: observable,
      isRunning: observable,
      isActive: observable,
      lastResult: observable,
      lastError: observable,
      executionCount: observable,
      totalExecutionTime: observable,
      lastExecutionTime: observable,
      lastExecutionDuration: observable,
      lastRpcMetrics: observable,
      rpcMetricsHistory: observable,
      chunkSize: observable,
      execute: action,
      start: action,
      stop: action,
      changeInterval: action,
      forceExecute: action,
      dispose: action,
      setChunkSize: action,
    })
  }

  /**
   * Execute the query function immediately and call appropriate callbacks
   * Respects rate limiting if configured, ensuring minimum time between executions
   * @param now - Optional timestamp for testing. Defaults to Date.now()
   */
  async execute(now: number = Date.now()): Promise<T> {
    // Rate limiting: wait if needed before executing
    // Uses lastExecutionTime (completion time) to ensure minimum interval between executions
    if (this.rateLimit) {
      const timeSinceLastExecution = now - this.lastExecutionTime

      if (timeSinceLastExecution < this.rateLimit.minIntervalMs) {
        const waitTime = this.rateLimit.minIntervalMs - timeSinceLastExecution
        await new Promise((resolve) => setTimeout(resolve, waitTime))
      }
    }

    const startTime = Date.now()

    // Create collector if metrics enabled
    if (this.orchestratedQueryStore.rpcMetricsEnabled) {
      this.rpcMetricsCollector = new RpcMetricsCollector()
    }

    // Set isRunning to true at the start
    runInAction(() => {
      this.isRunning = true
    })

    try {
      // Pass interface (this) to queryFn
      const result = await this.queryFn(this)

      const executionTime = Date.now()
      const duration = executionTime - startTime

      // Get metrics if collector was used
      const rpcMetrics = this.rpcMetricsCollector?.getMetrics()

      runInAction(() => {
        this.lastResult = result
        this.lastError = undefined
        this.executionCount++
        this.totalExecutionTime += duration
        this.lastExecutionTime = executionTime
        this.lastExecutionDuration = duration

        if (rpcMetrics) {
          this.lastRpcMetrics = rpcMetrics
          this.rpcMetricsHistory.push(rpcMetrics)
          if (this.rpcMetricsHistory.length > this.maxMetricsHistory) {
            this.rpcMetricsHistory.shift()
          }
        }
      })

      // Call success callback
      this.onQuerySuccess(this, result)

      return result
    } catch (error) {
      const executionTime = Date.now()
      const duration = executionTime - startTime

      // Get metrics even on error
      const rpcMetrics = this.rpcMetricsCollector?.getMetrics()

      runInAction(() => {
        this.lastError = error as Error
        this.executionCount++
        this.totalExecutionTime += duration
        this.lastExecutionTime = executionTime
        this.lastExecutionDuration = duration

        if (rpcMetrics) {
          this.lastRpcMetrics = rpcMetrics
          this.rpcMetricsHistory.push(rpcMetrics)
          if (this.rpcMetricsHistory.length > this.maxMetricsHistory) {
            this.rpcMetricsHistory.shift()
          }
        }
      })

      // Call failure callback
      this.onQueryFailed(this, error as Error)

      // console.log(`❌ OrchestratedQuery "${this.name}": Execution failed after ${duration}ms`)
      throw error
    } finally {
      // Clear collector after execution (session-only)
      this.rpcMetricsCollector = undefined

      // Always set isRunning to false, regardless of success, error, or callback exceptions
      runInAction(() => {
        this.isRunning = false
      })
    }
  }

  /**
   * Start automatic execution at the configured interval
   */
  start(): void {
    if (this.isActive) {
      return
    }

    this.isActive = true

    // Execute immediately
    this.execute()

    // Set up interval for future executions
    this.intervalId = setInterval(() => {
      this.execute()
    }, this.intervalMs)
  }

  /**
   * Stop the query interval
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    this.isActive = false
    // console.log(`⏹️ OrchestratedQuery "${this.name}": Stopped`)
  }

  /**
   * Change the interval timing and restart if active
   */
  changeInterval(intervalMs: number): void {
    this.intervalMs = intervalMs

    // If currently active, restart with new interval
    if (this.isActive) {
      this.stop()
      this.start()
    }
  }

  /**
   * Force immediate execution, bypassing any intervals and activating intervals
   */
  forceExecute(): Promise<T> {
    // Activate if not already
    if (!this.isActive) {
      this.isActive = true
    }

    // Execute immediately
    const result = this.execute()

    // Restart the interval if the query was active
    if (this.isActive) {
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
      this.intervalId = setInterval(() => {
        this.execute()
      }, this.intervalMs)
    }

    return result
  }

  /**
   * Get average execution time
   */
  get averageExecutionTime(): number {
    return this.executionCount > 0 ? this.totalExecutionTime / this.executionCount : 0
  }

  /**
   * Get time since last execution
   */
  get secondsSinceLastRun(): number {
    if (this.lastExecutionTime === 0) return Infinity
    return Math.floor((Date.now() - this.lastExecutionTime) / 1000)
  }

  // Getters for observable properties
  get getName(): string {
    return this.name
  }

  get getUniqueId(): string {
    return this.uniqueId
  }

  get getIsRunning(): boolean {
    return this.isRunning
  }

  get getIsActive(): boolean {
    return this.isActive
  }

  get getLastResult(): T | undefined {
    return this.lastResult
  }

  get getLastError(): Error | undefined {
    return this.lastError
  }

  get getExecutionCount(): number {
    return this.executionCount
  }

  get getTotalExecutionTime(): number {
    return this.totalExecutionTime
  }

  get getLastExecutionTime(): number {
    return this.lastExecutionTime
  }

  get getLastExecutionDuration(): number {
    return this.lastExecutionDuration
  }

  get getIntervalMs(): number {
    return this.intervalMs
  }

  /**
   * Get the RPC metrics collector for this query execution.
   * Implements IOrchestratedQuerySender interface.
   * Returns undefined if metrics collection is not enabled or no collector is active.
   */
  getRpcMetricsCollector(): RpcMetricsCollector | undefined {
    return this.rpcMetricsCollector
  }

  /**
   * Get last RPC execution metrics
   */
  get getLastRpcMetrics(): RpcExecutionMetrics | undefined {
    return this.lastRpcMetrics
  }

  /**
   * Get RPC metrics history
   */
  get getRpcMetricsHistory(): RpcExecutionMetrics[] {
    return [...this.rpcMetricsHistory]
  }

  /**
   * Get current chunk size (or undefined if query doesn't support chunking)
   */
  getChunkSize(): number | undefined {
    return this.chunkSizeBounds ? this.chunkSize : undefined
  }

  /**
   * Get chunk size bounds configuration (for UI to read min/max)
   */
  getChunkSizeBounds(): ChunkSizeBounds | undefined {
    return this.chunkSizeBounds
  }

  /**
   * Set chunk size with bounds validation
   * @param size - New chunk size value
   * @throws Error if query doesn't support chunking or value is out of bounds
   */
  setChunkSize(size: number): void {
    if (!this.chunkSizeBounds) {
      throw new Error(`Query "${this.name}" does not support chunk size configuration`)
    }

    if (size < this.chunkSizeBounds.min || size > this.chunkSizeBounds.max) {
      throw new Error(
        `Chunk size must be between ${this.chunkSizeBounds.min} and ${this.chunkSizeBounds.max} for query "${this.name}"`,
      )
    }

    this.chunkSize = size
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stop()
    this.orchestratedQueryStore.unregisterQuery(this)
  }
}
